import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import Button from '../../../../components/Button';
import IconButton from '../../../../components/IconButton';
import Spinner from '../../../../svgs/Spinner';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsIcon,
} from '@heroicons/react/outline';
import { Resource, Calendar } from './types';
import { Tab } from '@headlessui/react';
import { CalendarViewType, ViewMode } from '../utils/enums';
import { ViewSelector } from './ViewSelector';
import { CalendarViewTypeSelector } from './CalendarViewTypeSelector';
import { CalendarFilterMenu } from './CalendarFilterMenu';
import { logger } from '../../../../../lib/logger';
import { resolveCalendarAnchorDate } from '../utils/dateTimeUtils';

interface CalendarHeaderProps {
  viewType: CalendarViewType;
  viewMode: ViewMode;
  onViewTypeChange: (type: CalendarViewType) => void;
  onViewModeChange: (mode: ViewMode) => void;
  onTodayClick: () => void;
  onPrevClick: () => void;
  onNextClick: () => void;
  isLoading: boolean;
  isLoadingCalendars: boolean;
  isLoadingResources: boolean;
  calendars: Calendar[];
  resources: Resource[];
  selectedCalendarIds: string[];
  selectedResourceIds: string[];
  onCalendarSelect: (ids: string[]) => void;
  onResourceSelect: (ids: string[]) => void;
  dateRange: {
    start: string;
    end: string;
    currentStart?: string;
    timestamp: number;
  } | null;
}

export const CalendarHeader: FC<CalendarHeaderProps> = ({
  viewType,
  viewMode,
  onViewTypeChange,
  onViewModeChange,
  onTodayClick,
  onPrevClick,
  onNextClick,
  isLoading,
  isLoadingCalendars,
  isLoadingResources,
  calendars,
  resources,
  selectedCalendarIds,
  selectedResourceIds,
  onCalendarSelect,
  onResourceSelect,
  dateRange,
}) => {
  const navigate = useNavigate();

  const getDateRangeTitle = () => {
    if (!dateRange) return '';

    const startDate = DateTime.fromISO(dateRange.start);
    const endDate = DateTime.fromISO(dateRange.end).minus({ minutes: 1 });
    // Prefer currentStart for month titles — active start spills into the prior month in dayGrid.
    // Legacy persisted ranges without currentStart use the range midpoint.
    const currentStartDate = resolveCalendarAnchorDate(dateRange);

    logger.debug('🎯 getDateRangeTitle:', {
      startDate,
      endDate,
      currentStartDate,
    });

    switch (viewMode) {
      case ViewMode.DAY_GRID_MONTH:
        return currentStartDate.toFormat('MMMM yyyy');
      case ViewMode.TIME_GRID_WEEK:
        // Note: we need to add 1 week to the start date to get the correct week
        // library starts its weeks on Mondays.
        const weekStart = startDate
          .startOf('week')
          .plus({ weeks: 1 })
          .minus({ days: 1 });
        const weekEnd = weekStart.plus({ days: 6 });
        return `${weekStart.toFormat('MMM d')} - ${weekEnd.toFormat(
          'MMM d, yyyy',
        )}`;
      case ViewMode.TIME_GRID_DAY:
        return startDate.toFormat('EEEE, MMMM d, yyyy');
      case ViewMode.RESOURCE_TIME_GRID_DAY:
        return `${startDate.toFormat('MMM d')} - ${endDate.toFormat(
          'MMM d, yyyy',
        )}`;
      case ViewMode.RESOURCE_TIMELINE_MONTH:
        return currentStartDate.toFormat('MMMM yyyy');
      default:
        return currentStartDate.toFormat('MMMM yyyy');
    }
  };

  return (
    <header className="flex flex-none items-center justify-between border-b border-neutral-75 px-0 pb-4">
      <div className="flex h-[39px] w-full flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          <Tab.Group
            selectedIndex={viewType === CalendarViewType.CALENDARS ? 0 : 1}
            onChange={(index) =>
              onViewTypeChange(
                index === 0
                  ? CalendarViewType.CALENDARS
                  : CalendarViewType.RESOURCES,
              )
            }
          >
            <Tab.List className="mr-4 flex space-x-1 rounded-xl bg-neutral-50 p-1">
              <Tab
                className={({ selected }) =>
                  `text-sm rounded-lg px-3 py-1 font-medium focus:outline-none focus:ring-2 focus:ring-secondary-50 ${
                    selected
                      ? 'bg-white text-green-150 shadow'
                      : 'text-neutral-125 hover:text-green-150'
                  }`
                }
              >
                Calendars
              </Tab>
              <Tab
                className={({ selected }) =>
                  `text-sm rounded-lg px-3 py-1 font-medium focus:outline-none focus:ring-2 focus:ring-secondary-50 ${
                    selected
                      ? 'bg-white text-green-150 shadow'
                      : 'text-neutral-125 hover:text-green-150'
                  }`
                }
              >
                Rooms
              </Tab>
            </Tab.List>
          </Tab.Group>

          <CalendarViewTypeSelector
            viewType={viewType}
            currentView={viewMode}
            onViewChange={onViewModeChange}
          />

          <div className="ml-4 flex items-center space-x-2">
            <Button
              title="Today"
              theme="secondary"
              size="small"
              className="px-3"
              onClick={onTodayClick}
            />
            <div className="flex items-center space-x-1">
              <IconButton
                aria-label="Previous"
                IconComponent={ChevronLeftIcon}
                className="h-7 w-7 rounded-md border border-neutral-75 text-neutral-125 hover:border-green-150 hover:text-green-150"
                iconClassName="h-5 w-5 min-w-[18px] min-h-[18px]"
                onClick={onPrevClick}
              />
              <IconButton
                aria-label="Next"
                IconComponent={ChevronRightIcon}
                className="h-7 w-7 rounded-md border border-neutral-75 text-neutral-125 hover:border-green-150 hover:text-green-150"
                iconClassName="h-5 w-5 min-w-[18px] min-h-[18px]"
                onClick={onNextClick}
              />
            </div>
          </div>
          <span className="text-sm ml-4 mr-4 truncate whitespace-nowrap font-medium text-neutral-125">
            {getDateRangeTitle()}
          </span>
        </div>

        <div className="flex items-center space-x-4">
          <div className="w-7">
            {isLoading && <Spinner className="h-5 w-5" />}
          </div>
          <CalendarFilterMenu />
          <div className="min-w-[90px]">
            {viewType === CalendarViewType.CALENDARS ? (
              <ViewSelector
                items={calendars}
                selectedIds={selectedCalendarIds}
                onChange={onCalendarSelect}
                getItemId={(calendar) => calendar.id}
                getItemLabel={(calendar) => calendar.name}
                loading={isLoadingCalendars}
              />
            ) : (
              <ViewSelector
                items={resources}
                selectedIds={selectedResourceIds}
                onChange={onResourceSelect}
                getItemId={(resource) => resource.id}
                getItemLabel={(resource) => resource.name}
                loading={isLoadingResources}
                splitBy={null}
                truncateAfter={15}
              />
            )}
          </div>
          <IconButton
            aria-label="Calendar integration"
            IconComponent={AdjustmentsIcon}
            iconClassName="text-green-150 h-5 w-5 min-w-[20px] min-h-[20px] rotate-90"
            onClick={() => navigate('/integrations/calendar')}
          />
        </div>
      </div>
    </header>
  );
};
